import { useTranslation } from 'react-i18next';
import { MessageSquare, RefreshCw } from 'lucide-react';
import {
  FieldSectionLabel,
  IdentityAvatar,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@bliks-io/ui';
import type { DraftStepSurveyStatus } from '@bliks-io/contracts';

type SurveyRecipient = DraftStepSurveyStatus['recipients'][number];
type SurveyRecipientStatus = SurveyRecipient['status'];
type VariantStatus = SurveyRecipient['variantStatus'];

/** Status dot colour per survey recipient: done=green, dismissed=slate (they
 *  answered "Ich mache diesen Schritt nicht" — attributed on purpose, ADR 0022,
 *  because the modeler has to correct the performer set), pending=amber,
 *  to-be-created (or staged re-request)=blue, invited-but-unaccepted=grey (no
 *  survey yet — they're surfaced for the modeler, not surveyed until they
 *  accept). */
function statusDotClass(status: SurveyRecipientStatus): string {
  if (status === 'completed') return 'bg-positive-solid';
  if (status === 'dismissed') return 'bg-fill-neutral';
  if (status === 'toCreate') return 'bg-info-solid';
  if (status === 'invited') return 'bg-surface-strong';
  return 'bg-warning-solid';
}

/** Translated status text for a recipient (literal `t()` keys so the
 *  translation scanner sees them as used). */
function statusLabel(status: SurveyRecipientStatus, t: (key: string) => string): string {
  if (status === 'completed') return t('processDraft.surveys.status.completed');
  if (status === 'dismissed') return t('processDraft.surveys.status.dismissed');
  if (status === 'toCreate') return t('processDraft.surveys.status.toCreate');
  if (status === 'invited') return t('processDraft.surveys.status.invited');
  return t('processDraft.surveys.status.pending');
}

/** Where the recipient stands on the Variant Check, which `status` cannot
 *  express since the verdict became its own document: a `pending` row says
 *  nothing about whether the variant question was ever put to them
 *  (`notApplicable` — author, assigned User, or the check is off for them) or
 *  already answered. */
function variantStatusLabel(status: VariantStatus, t: (key: string) => string): string {
  if (status === 'answered') return t('processDraft.surveys.variantStatus.answered');
  if (status === 'pending') return t('processDraft.surveys.variantStatus.pending');
  return t('processDraft.surveys.variantStatus.notApplicable');
}

interface Props {
  resurveyRequested: boolean;
  surveyStatus: DraftStepSurveyStatus | undefined;
  onToggleResurvey: (next: boolean) => void;
}

/**
 * The survey block of a manual draft step: a re-request toggle plus the
 * per-performer recipient list with live status dots (ADR 0009). Rendered only
 * for survey-eligible steps (human performers).
 */
export function DraftStepSurveySection({
  resurveyRequested,
  surveyStatus,
  onToggleResurvey
}: Props) {
  const { t } = useTranslation();

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between">
        <FieldSectionLabel icon={MessageSquare}>
          {t('processDraft.surveys.heading')}
        </FieldSectionLabel>
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => onToggleResurvey(!resurveyRequested)}
                aria-label={t('processDraft.surveys.reRequestToggleLabel')}
                aria-pressed={resurveyRequested}
                className={`flex h-7 items-center gap-1.5 rounded-md border px-2 text-xs font-medium transition-colors ${
                  resurveyRequested
                    ? 'border-info bg-info text-white hover:bg-info-strong'
                    : 'border-transparent text-muted-foreground hover:bg-muted'
                }`}
              >
                <RefreshCw className="h-3.5 w-3.5" />
                {resurveyRequested
                  ? t('processDraft.surveys.reRequestShortActive')
                  : t('processDraft.surveys.reRequestShort')}
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-60">
              {t('processDraft.surveys.reRequestToggleHint')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      {surveyStatus && surveyStatus.recipients.length > 0 ? (
        <ul className="mt-2 space-y-1.5">
          {surveyStatus.recipients.map((r, i) => {
            // Staging a re-request (re)creates every survey, so show them
            // all as "to create" while it's active — except invited peers,
            // who aren't surveyed until they accept.
            const recipientStatus =
              resurveyRequested && r.status !== 'invited' ? 'toCreate' : r.status;
            // The re-request bumps the generation, so a stored answer stops
            // pairing with the row and the question is open again.
            const variantStatus =
              resurveyRequested && r.variantStatus === 'answered' ? 'pending' : r.variantStatus;
            return (
              <li key={`${r.name}-${i}`} className="flex items-center gap-2">
                <IdentityAvatar identity={r} />
                <span className="flex-1 truncate text-sm text-foreground">{r.name}</span>
                <span className="flex shrink-0 items-center gap-1.5">
                  <span className={`h-1.5 w-1.5 rounded-full ${statusDotClass(recipientStatus)}`} />
                  <span className="text-xs text-muted-foreground">
                    {statusLabel(recipientStatus, t)} · {variantStatusLabel(variantStatus, t)}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="mt-2 text-xs text-fg-subtle">{t('processDraft.surveys.statusNone')}</p>
      )}
    </div>
  );
}
